import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentAuthorizationService,
  AppointmentModel,
  AvailityServiceReview,
  ClinicService,
  CrudService,
  FacilityService,
  filterAction,
  INSURANCE_PROVIDER_TRANSACTION_TYPE,
  InsuranceProviderConfiguration,
  InsuranceProviderConfigurationService,
  InsuranceProviderLookup,
  InsuranceProviderLookupsService,
  Patient,
  PatientDataService,
  ProductModel,
  ReferralModel,
  stripPhone,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { filter as _filter, flatten, uniq } from 'lodash'
import {
  type Observable,
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs'

import { toDotObject } from '../../../prior-authorization-form'
import type { ServiceReviewInquiryFormValue } from '../service-review-inquiry-form'

export interface ServiceReviewInquiryDialogData {
  patient: Patient
  type: 'referrals' | 'authorizations'
  appointment: AppointmentModel
  products?: ProductModel[]
}

export interface PullPriorAuthorizationPayload {
  appointmentId: string
  patientId: string
  payerId: string
  firstName: string
  lastName: string
  dateOfBirth: string
  providerId: string
  serviceTypeCode: string
  memberId: string
  dateFrom: string
  dateTo: string
  placeOfServiceCode: string
}

export interface PullPriorAuthorizationResponse {
  action: string
  data: AvailityServiceReview.Review[]
  message: string
  topic: string
}

export type PullPriorAuthorizationMode = 'request' | 'results'

@Component({
  templateUrl: './pull-prior-authorization.component.html',
  styleUrls: ['./pull-prior-authorization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceReviewInquiryDialogComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>()
  public isLoading$ = new BehaviorSubject<boolean>(false)
  public noSelection = false

  public readonly SUBTYPE = InsuranceProviderConfiguration.SUBTYPE_ID

  public loading$ = combineLatest([
    this.isLoading$,
    this.insuranceProviderLookupsService.loading$,
    this.insuranceProviderConfigurationService.isLoading$,
  ]).pipe(
    map(([isLoading, isLookupsLoading, isConfigurationLoading]) => isLoading || isLookupsLoading || isConfigurationLoading),
  )

  public mode$ = new BehaviorSubject<PullPriorAuthorizationMode>('request')

  public availableServiceTypes: InsuranceProviderConfiguration.SUBTYPE_ID[]
  public availableCarriers$: Observable<InsuranceProviderLookup[]> = this.patientDataService.insurancePairs$.pipe(
    takeUntil(this.onDestroy$),
    map(pairs => pairs.filter(([, lookup]) =>
      this.insuranceProviderLookupsService.doesLookupSupport(lookup, INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY),
    ).map(pair => pair[1])),
  )

  public formGroup = this.fb.group({
    carrier: this.fb.control<InsuranceProviderLookup>(null, Validators.required),
    requestTypeCode: this.fb.control<InsuranceProviderConfiguration.SUBTYPE_ID>(null, Validators.required),
    priorAuthorization: this.fb.control(null, Validators.required),
    serviceReviews: this.fb.control<AvailityServiceReview.Review[]>(null, [Validators.required, Validators.minLength(1)]),
  })

  public serviceReviews$: Observable<AvailityServiceReview.Review[]> = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      filter((results) => !results.data.errors),
      map((results) => results.data),
      tap((serviceReviews) => {
        this.isLoading$.next(false)
        if (!serviceReviews) {
          this.mode$.next('request')
          return
        }

        this.mode$.next('results')
      }),
      shareReplay(1),
    )

  public serviceReviewErrors$ = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      filter((results) => !!results.data.errors),
      map((results) => results.data.errors),
      tap(() => this.isLoading$.next(false)),
    )

  public resultsVisible$ = combineLatest([
    this.mode$,
    this.serviceReviews$,
  ])
    .pipe(
      map(([mode, serviceReviews]) => mode === 'results' && !!serviceReviews),
    )

  public procedureCodes: string[] = []

  constructor(
    public dialogRef: MatDialogRef<ServiceReviewInquiryDialogComponent>,
    public insuranceProviderConfigurationService: InsuranceProviderConfigurationService,
    @Inject(MAT_DIALOG_DATA) public data: ServiceReviewInquiryDialogData,
    private readonly patientDataService: PatientDataService,
    private readonly websocketService: WebSocketService,
    private readonly fb: FormBuilder,
    private readonly authorizationService: AppointmentAuthorizationService,
    private readonly crudService: CrudService,
    private readonly snackbar: MatSnackBar,
    private readonly clinicService: ClinicService,
    private readonly facilityService: FacilityService,
    private readonly insuranceProviderLookupsService: InsuranceProviderLookupsService,
  ) { }

  public ngOnInit(): void {
    this.availableServiceTypes = this.data.type === 'referrals'
      ? [InsuranceProviderConfiguration.SUBTYPE_ID.REFERRALS]
      : [InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES, InsuranceProviderConfiguration.SUBTYPE_ID.ADMISSION]
    const defaultServiceReviewType = this.availableServiceTypes[0]

    this.procedureCodes = _filter(uniq(flatten(this.data.appointment.products.map(product => product.codes))))

    this.formGroup.controls.carrier.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      startWith(null),
      combineLatestWith(this.formGroup.controls.requestTypeCode.valueChanges.pipe(
        takeUntil(this.onDestroy$),
      )),
      filter(([lookup]) => lookup.provider?.slug != null),
      tap(([lookup, requestType]) => this.requestConfiguration(lookup, requestType)),
    ).subscribe()

    this.availableCarriers$.pipe(
      takeUntil(this.onDestroy$),
      filter(lookups => lookups.length > 0),
    ).subscribe(lookups => {
      this.formGroup.controls.carrier.setValue(lookups[0])
      this.formGroup.patchValue({
        requestTypeCode: defaultServiceReviewType,
      })
    })
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onSubmit(mode: PullPriorAuthorizationMode) {
    switch (mode) {
      case 'request':
        this.isLoading$.next(true)
        this.websocketService.messageQueue.next({
          action: WebsocketAction.PRIOR_AUTHORIZATION_INDEX,
          appointmentId: this.data.appointment.id,
          patientId: this.data.patient.id,
          providerLookupId: this.formGroup.value.carrier.id,
          providerId: this.formGroup.value.carrier?.carrierId,
          data: this.getPayload(),
        })
        break
      case 'results':
        if (this.formGroup.value.serviceReviews?.length > 0) {
          this.saveSelectedServiceReviews()
        } else {
          this.noSelection = true
        }
        break
    }
  }

  public requestConfiguration(lookup: InsuranceProviderLookup, requestType: InsuranceProviderConfiguration.SUBTYPE_ID) {
    this.insuranceProviderConfigurationService.configurationParams$.next({
      providerLookupId: lookup.id,
      payerId: lookup.provider?.slug,
      type: InsuranceProviderConfiguration.TYPE.SERVICE_REVIEWS_INQUIRY,
      subtypeId: requestType,
    })
  }

  private getPayload(): PullPriorAuthorizationPayload {
    const requestTypeCode = this.formGroup.value.requestTypeCode
    const inquiryFormValue = (this.formGroup.value.priorAuthorization ?? {}) as ServiceReviewInquiryFormValue
    const facility = this.facilityService.current$.getValue()
    const carrier = this.formGroup.value.carrier

    return toDotObject({
      payer: {
        id: carrier.provider.slug,
      },
      requestTypeCode: requestTypeCode ?? InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
      fromDate: inquiryFormValue?.fromDate?.format('YYYY-MM-DD'),
      toDate: inquiryFormValue?.toDate?.format('YYYY-MM-DD'),
      certificationIssueDate: inquiryFormValue?.certificationIssueDate?.format('YYYY-MM-DD'),
      certificationNumber: inquiryFormValue?.certificationNumber,
      referenceNumber: inquiryFormValue?.referenceNumber,
      requestingProvider: {
        lastName: this.clinicService.clinic.provider.name,
        npi: this.clinicService.clinic.provider.id,
        taxId: this.clinicService.clinic.provider.taxId,
        addressLine1: facility.address?.address1,
        city: facility.address?.city,
        stateCode: facility.address?.state,
        zipCode: facility.address?.zip,
        contactName: this.clinicService.clinic.provider.name,
        phone: stripPhone(this.clinicService.clinic.phoneNumber),
        roleCode: InsuranceProviderConfiguration.ROLE_CODE.FACILITY,
      },
      subscriber: {
        lastName: this.data.patient?.name.family,
        firstName: this.data.patient?.name.given,
        memberId: this.data.appointment?.insurances.find((insurance) => insurance.carrierId === carrier.carrierId )?.memberId,
      },
      patient: {
        lastName: this.data.patient?.name.family,
        firstName: this.data.patient?.name.given,
        middleName: this.data.patient?.name.middle,
        suffix: this.data.patient?.name.suffix,
        birthDate: this.data.patient?.dob.format('YYYY-MM-DD'),
      },
      // ...formValue,
    })
  }

  private saveSelectedServiceReviews() {
    this.isLoading$.next(true)

    if (this.data.type === 'referrals') {
      this.saveReferrals()
    } else {
      this.saveAuthorizations()
    }
  }

  private saveAuthorizations() {
    const carrier = this.formGroup.value.carrier
    const patientInsurance = this.data.appointment.insurances.find((insurance) => insurance.carrierLookupId === carrier.id)

    this.authorizationService.bulkCreateAuthorizations(
      this.data.appointment.id,
      carrier.id,
      patientInsurance.id,
      this.formGroup.value.serviceReviews,
    )
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.isLoading$.next(false)),
        catchError((e) => {
          this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })

          return of(null)
        }),
      )
      .subscribe((authorizationList) => {
        if (authorizationList) {
          this.dialogRef.close(authorizationList)
        }
      })
  }

  private saveReferrals() {
    const carrier = this.formGroup.value.carrier
    const patientInsurance = this.data.appointment.insurances.find((insurance) => insurance.carrierLookupId === carrier.id)
    const serviceReviews = this.formGroup.value.serviceReviews

    const obs = serviceReviews.map(serviceReview => {
      const referral = new ReferralModel({
        patientId: this.data.patient.id,
        appointmentIds: [this.data.appointment.id],
        insuranceId: patientInsurance.id,
        carrierLookupId: patientInsurance.carrierLookupId,
        carrierId: patientInsurance.carrierId,
        number: serviceReview.certificationNumber,
      })
      return this.crudService.create(referral)
    })

    forkJoin(obs).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isLoading$.next(false)),
      catchError((e) => {
        this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })
        return of(null)
      }),
    ).subscribe((referrals) => {
      if (referrals) {
        this.dialogRef.close(referrals)
      }
    })
  }
}
